import { useState, useEffect, useCallback } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../../../config/firebase';

/**
 * QuizSectionのコンテンツ管理を担当するカスタムフック
 * 
 * @param {Object} currentUser - 現在のユーザー情報
 * @param {Object} selectedStudent - 選択された生徒情報
 * @param {boolean} isTeacher - 教師かどうかのフラグ
 * @returns {Object} コンテンツ管理に関する状態とメソッド
 */
const useQuizContent = (currentUser, selectedStudent, isTeacher) => {
  // 状態管理
  const [contents, setContents] = useState([]);
  const [assignedContents, setAssignedContents] = useState([]);
  const [assignedContentsDetails, setAssignedContentsDetails] = useState([]);
  const [error, setError] = useState(null);

  // 生徒IDの決定
  const studentId = selectedStudent ? selectedStudent.id : currentUser.id;

  // 全コンテンツの取得
  useEffect(() => {
    if (!isTeacher) {
      setContents([]);
      return;
    }

    const contentsRef = ref(database, 'contents');
    const unsubscribe = onValue(contentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const contentsArray = Object.entries(data).map(([id, content]) => ({
          id,
          ...content
        }));
        setContents(contentsArray);
      } else {
        setContents([]);
      }
    }, (error) => {
      console.error('コンテンツの取得に失敗しました:', error);
      setError('コンテンツの取得に失敗しました。');
    });

    return () => unsubscribe();
  }, [isTeacher]);

  // 割り当てられたコンテンツの取得
  useEffect(() => {
    if (!studentId) {
      setAssignedContents([]);
      return;
    }

    const assignedRef = ref(database, `studentAssignedContents/${studentId}`);
    const unsubscribe = onValue(assignedRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const assignedArray = Object.keys(data).filter(contentId => data[contentId] === true);
        setAssignedContents(assignedArray);
      } else {
        setAssignedContents([]);
      }
    }, (error) => {
      console.error('割り当てコンテンツの取得に失敗しました:', error);
      setError('割り当てコンテンツの取得に失敗しました。');
    });

    return () => unsubscribe();
  }, [studentId]);

  // 割り当てられたコンテンツの詳細取得
  useEffect(() => {
    if (assignedContents.length === 0) {
      setAssignedContentsDetails([]);
      return;
    }

    const fetchAssignedContentsDetails = async () => {
      try {
        const promises = assignedContents.map(contentId => {
          return new Promise((resolve, reject) => {
            const contentRef = ref(database, `contents/${contentId}`);
            onValue(contentRef, (snapshot) => {
              const content = snapshot.val();
              if (content) {
                resolve({ id: contentId, ...content });
              } else {
                resolve(null);
              }
            }, reject, { onlyOnce: true });
          });
        });

        const results = await Promise.all(promises);
        const validResults = results.filter(Boolean);
        setAssignedContentsDetails(validResults);
      } catch (error) {
        console.error('割り当てコンテンツ詳細の取得に失敗しました:', error);
        setError('割り当てコンテンツ詳細の取得に失敗しました。');
      }
    };

    fetchAssignedContentsDetails();
  }, [assignedContents]);

  // エラークリア関数
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // コンテンツフィルタリング関数
  const getFilteredContents = useCallback((activeSubject) => {
    if (activeSubject === 'all') {
      return assignedContentsDetails;
    }
    return assignedContentsDetails.filter(content => 
      content.subject === activeSubject
    );
  }, [assignedContentsDetails]);

  // コンテンツ検索関数
  const findContentById = useCallback((contentId) => {
    return assignedContentsDetails.find(content => content.id === contentId);
  }, [assignedContentsDetails]);

  // 全コンテンツから検索
  const findContentInAll = useCallback((contentId) => {
    return contents.find(content => content.id === contentId);
  }, [contents]);

  return {
    // 状態
    contents,
    assignedContents,
    assignedContentsDetails,
    error,
    studentId,

    // メソッド
    clearError,
    getFilteredContents,
    findContentById,
    findContentInAll
  };
};

export default useQuizContent; 